import { Button, Card, Loading, Option, Select, Text, Tooltip } from '@sede-enterprise/shell-ds-react-framework'
import React, { useEffect, useState } from 'react'
import Plot from 'react-plotly.js'
import { InfoCircle } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import useLTRemanentLife from '../../hooks/useLTRemanentLife';
import useSTRemanentLife from '../../hooks/useSTRemanentLife';
import CustomPieLegend from '../../../../components/CustomPieLegend/CustomPieLegend';


const currentDate = new Date();
const maxExpiryDate = new Date();
maxExpiryDate.setFullYear(currentDate.getFullYear() + 20);
const currentDateISO = currentDate.toISOString().split('T')[0];
const maxExpiryDateISO = maxExpiryDate.toISOString().split('T')[0];


const RemanentLifeChart = ({ filter }) => {
    const { data: ltRemanentData, loading: ltRemanentLoading, error: ltRemanentError } = useLTRemanentLife(filter, currentDateISO, maxExpiryDateISO);
    const { data: stRemanentData, loading: stRemanentLoading, error: stRemanentError } = useSTRemanentLife(filter, currentDateISO, maxExpiryDateISO);

    const [remanentData, setRemanentData] = useState([]);
    const [remanentCriteria, setRemanentCriteria] = useState();

    // Compare 1st object from ltRemanentData and stRemanentData and set the remanentData
    useEffect(() => {

        if (!ltRemanentData || ltRemanentData.length === 0) {
            setRemanentData(stRemanentData);
            setRemanentCriteria('ST');
            return;
        }

        if (!stRemanentData || stRemanentData.length === 0) {
            setRemanentData(ltRemanentData);
            setRemanentCriteria('LT');
            return;
        }

        const ltRemanentDate = new Date(ltRemanentData.LTpredictedExpiryDate);
        const stRemanentDate = new Date(stRemanentData.STpredictedExpiryDate);

        if (ltRemanentDate < stRemanentDate) {
            setRemanentData(ltRemanentData);
            setRemanentCriteria('LT');
        } else {
            setRemanentData(stRemanentData);
            setRemanentCriteria('ST');
        }

    }, [ltRemanentData, stRemanentData])


    // Set remanentData based on the criteria
    useEffect(() => {
        if (remanentCriteria === 'LT') {
            setRemanentData(ltRemanentData);
        } else {
            setRemanentData(stRemanentData);
        }
    }, [remanentCriteria, ltRemanentData, stRemanentData])




    const [category, setCategory] = useState();

    const [greenCategory, setGreenCategory] = useState([]);
    const [amberCategory, setAmberCategory] = useState([]);
    const [redCategory, setRedCategory] = useState([]);
    const [remanentPieData, setRemanentPieData] = useState([]);

    useEffect(() => {
        let dataArray = [];
        if (Array.isArray(remanentData)) {
            dataArray = remanentData;
        } else if (remanentData && typeof remanentData === 'object') {
            dataArray = [remanentData];
        }

        if (dataArray.length > 0) {
            const red = [];
            const amber = [];
            const green = [];
            dataArray.forEach((obj) => {
                const expiryDate = new Date(obj.LTpredictedExpiryDate || obj.STpredictedExpiryDate);
                const diffYears = (expiryDate - currentDate) / (1000 * 60 * 60 * 24 * 365);

                if (diffYears < 5) {
                    red.push(obj);
                } else if (diffYears >= 5 && diffYears < 10) {
                    amber.push(obj);
                } else {
                    green.push(obj);
                }
            });

            // Determine the default category
            let defaultCategory = 'green';
            if (red.length > 0) {
                defaultCategory = 'red';
            } else if (amber.length > 0) {
                defaultCategory = 'amber';
            }

            setRemanentPieData([{
                labels: [`<5 years`, `5 - 10 years`, `>10 years`],
                values: [red.length, amber.length, green.length],
                type: 'pie',
                hole: 0.5,
                automargin: true,
                textinfo: "none",
                hoverinfo: "label+value",
                marker: {
                    colors: ['#d93638', '#fbcf09', '#197647'],


                    line: {
                        color: ['rgba(28, 28, 28, 0.38)', "rgba(28, 28, 28, 0.38)", "rgba(28, 28, 28, 0.38)"], // Border colors for each slice
                        width: [
                            defaultCategory === 'red' ? 5 : 0,
                            defaultCategory === 'amber' ? 5 : 0,
                            defaultCategory === 'green' ? 5 : 0,
                        ], // Highlight the default category
                    },

                },
            }]);


            // Update the default category
            setCategory(defaultCategory);

            if (red.length > 0) {
                setRedCategory([{
                    x: red.map((obj) => obj.LTpredictedExpiryDate || obj.STpredictedExpiryDate),
                    y: red.map((obj) => obj.id), // Make y unique
                    text: red.map((obj) => obj.name), // Display value
                    type: 'bar',
                    orientation: 'h',
                    marker: { color: '#d93638' },
                    textposition: 'none', // Prevent text inside bars
                }]);
            }
            if (amber.length > 0) {
                setAmberCategory([{
                    x: amber.map((obj) => obj.LTpredictedExpiryDate || obj.STpredictedExpiryDate),
                    y: amber.map((obj) => obj.id), // Make y unique
                    text: amber.map((obj) => obj.name), // Display value
                    type: 'bar',
                    orientation: 'h',
                    marker: { color: '#fbcf09' },
                    textposition: 'none', // Prevent text inside bars
                }]);
            }
            if (green.length > 0) {
                setGreenCategory([{
                    x: green.map((obj) => obj.LTpredictedExpiryDate || obj.STpredictedExpiryDate),
                    y: green.map((obj) => obj.id), // Make y unique
                    text: green.map((obj) => obj.name), // Display value
                    type: 'bar',
                    orientation: 'h',
                    marker: { color: '#197647' },
                    textposition: 'none', // Prevent text inside bars
                }]);
            }

            // Set the default category to red if redCategory has data else amber if amberCategory has data else green
            if (red.length > 0) {
                setCategory('red');
            }
            else if (amber.length > 0) {
                setCategory('amber');
            }
            else {
                setCategory('green');
            }

        }
    }, [remanentData])


    // Separate useEffect for updating the border width based on the selected category
    useEffect(() => {
        setRemanentPieData((prevData) => {
            if (prevData.length > 0) {
                return [{
                    ...prevData[0],
                    marker: {
                        ...prevData[0].marker,
                        line: {
                            ...prevData[0].marker.line,
                            width: [
                                category === 'red' ? 5 : 0,
                                category === 'amber' ? 5 : 0,
                                category === 'green' ? 5 : 0,
                            ], // Update border widths based on the selected category
                        },
                    },
                }];
            }
            return prevData;
        });
    }, [category]); // Runs whenever the category changes


    const onRemnantPieClick = (data) => {
        switch (data.points[0].pointNumber) {
            case 0:
                setCategory('red');
                break;
            case 1:
                setCategory('amber');
                break;
            case 2:
                setCategory('green');
                break;
            default:
                break;
        }
    }

    const loading = ltRemanentLoading || stRemanentLoading;
    const error = ltRemanentError || stRemanentError;


    // Assuming each bar requires 50px of vertical space and the base height is 150px
    const baseHeight = 150;
    const heightIncrement = 50;
    let numberOfBars;
    if (category === 'red') {
        numberOfBars = redCategory[0]?.x?.length;
    } else if (category === 'amber') {
        numberOfBars = amberCategory[0]?.x?.length;
    } else {
        numberOfBars = greenCategory[0]?.x?.length;
    }
    const totalHeight = baseHeight + (numberOfBars * heightIncrement);

    console.log("red category data:", redCategory);

    return (
        <div className='remanant-life-chart' id='remanent-id-1'>
            <div className='content-header'>
                <div className='remanent-header'>
                    <h2 id='remanent-id-3'>CMLs with the Shortest Remnant Life</h2>
                    <Tooltip className='remanent-tooltip' placement='bottom' overlay={<div className='tooltip-content' id='remanent-id-10'>Displaying the CMLs with less that 20 years of Remnant life</div>} trigger={<Button size='large' variant='transparent' iconOnly icon={<InfoCircle />} onClick={() => { }} />} />
                </div>

                <div className='remanent-select'>
                    <Select onChange={(_, option) => setRemanentCriteria(option.value)} value={remanentCriteria} allowClear={false}>
                        <Option value='LT' key='LT'>Long Term CR</Option>
                        <Option value='ST' key='ST'>Short Term CR</Option>
                    </Select>
                </div>
            </div>

            {loading ? <div className='loading-container' role='status' id='remanent-id-4'>
                <Loading id='remanent-id-5' />
            </div> :
                remanentData?.length === 0 || !!error ? <div className='empty-container' id='remanent-id-6'>
                    <Text id='remanent-id-7'>At present, no CMLs are classified under this category</Text>
                </div>
                    : remanentData?.length > 0 && <div className='remnant-charts-container' id='remanent-id-99'>
                        <Card className='chart-card remnant-card' bodyPadding={false} id='remanent-id-2'>
                            <div className='graphs-container remnant-chart pie-remnant' id='remanent-id-8'>

                                <Plot
                                    useResizeHandler={true}
                                    className="full-size-chart pointer"
                                    data={remanentPieData}
                                    config={{ displayModeBar: false }}
                                    layout={{
                                        legend: {
                                            itemclick: false,
                                            itemdoubleclick: false
                                        },
                                        showlegend: false,
                                        autosize: true,
                                        margin: { t: 20, b: 20 },
                                        height: 250,
                                    }}
                                    onClick={onRemnantPieClick}
                                />
                                <CustomPieLegend greenText='>10 years' amberText='5 - 10 years' redText='<5 years' />

                            </div>

                        </Card>
                        <Card className='chart-card remnant-card' bodyPadding={false} id='remanent-id-243'>
                            <div className='graphs-container remnant-chart' id='remanent-id-88'>
                                <Plot
                                    id='remanent-id-99'
                                    data={category === 'red' ? redCategory : category === 'amber' ? amberCategory : greenCategory}
                                    useResizeHandler={true}
                                    style={{ width: '100%', height: `${totalHeight}px` }}
                                    config={{ responsive: true, displayModeBar: false }}
                                    layout={{
                                        autosize: true,
                                        margin: {
                                            l: 150,
                                            r: 50,
                                            b: 80,
                                            t: 20,
                                        },
                                        xaxis: { title: 'Predicted Remaining Life', range: [currentDateISO, maxExpiryDateISO] },
                                        yaxis: category === 'red'
                                                ? { tickvals: redCategory[0]?.y, ticktext: redCategory[0]?.text }
                                                : category === 'amber'
                                                ? { tickvals: amberCategory[0]?.y, ticktext: amberCategory[0]?.text }
                                                : { tickvals: greenCategory[0]?.y, ticktext: greenCategory[0]?.text },
                                    }} />
                            </div>
                        </Card>
                    </div>
            }
        </div>
    )
}

export default RemanentLifeChart;